import { Country, Language } from './lists.model';
import { User } from './user.model';
import { toBoolean } from '../utils/utils';

/** Organisation.forcedBroadcastDelay, block 4 (C2027-195, PO Q16: saved only, not enforced yet). */
export type ForcedBroadcastDelay = 'NEVER' | 'DAYS_3' | 'WEEK_1' | null;

export class Organisation {
  id: number;

  name: string;
  urlLogo: string;
  address: string;
  addressExtra: string;
  postcode: string;
  city: string;
  country: Country;
  phone: string;

  employees: User[];

  sectorOwner: User;
  negoceOwner: User;

  defaultLanguageGui: Language;
  defaultLanguagePdf: Language;

  hasBeApi: boolean;
  hasI18n: boolean;

  // Block 4 of the organisation form (C2027-195): informative except sendAfterExpertValidation
  // and forcedBroadcastDelay, which are admin-only (organisation-field-access.ts).
  sendAfterExpertValidation: boolean;
  notifyAdvisorsOnAvailability: boolean;
  notifyManagerOnAvailability: boolean;
  wanakaBroadcastsToFarmers: boolean;
  wanakaMakesVisible: boolean;
  forcedBroadcastDelay: ForcedBroadcastDelay;

  // Block 6 of the organisation form (C2027-196): what a farmer-only user of this organisation
  // may do. All granted by default (PO Q25).
  farmerCanCreateFarm: boolean;
  farmerCanCreateParcel: boolean;
  farmerCanEditParcel: boolean;
  farmerCanCreateOrder: boolean;
  farmerCanEditOrder: boolean;
  farmerCanPlanDelivery: boolean;
}

/** Body of PUT /organisations/{id}/general (C2027-194). */
export interface OrganisationGeneralPayload {
  name: string;
  sectorOwnerId: number;
  negoceOwnerId: number | null;
  address: string;
  addressExtra: string;
  postcode: string;
  city: string;
  countryId: number;
  phone: string;
  // Only sent when the country changed: see OrganisationEditComponent.saveGeneral.
  defaultLanguageGuiId?: number;
  defaultLanguagePdfId?: number;
}

/** Body of PUT /organisations/{id}/customization (C2027-194). hasBeApi moved to block 3 (C2027-198). */
export interface OrganisationCustomizationPayload {
  hasI18n: boolean;
  defaultLanguageGuiId: number;
  defaultLanguagePdfId: number;
}

// A <select> bound with [value] returns a string id; the payload needs a number.
const idOf = (entity?: { id: number | string }): number => (entity && entity.id != null ? Number(entity.id) : null);

export function toGeneralPayload(org: Organisation, includeLanguages = false): OrganisationGeneralPayload {
  const payload: OrganisationGeneralPayload = {
    name: org.name,
    sectorOwnerId: idOf(org.sectorOwner),
    negoceOwnerId: org.negoceOwner && org.negoceOwner.id ? idOf(org.negoceOwner) : null,
    address: org.address,
    addressExtra: org.addressExtra,
    postcode: org.postcode,
    city: org.city,
    countryId: idOf(org.country),
    phone: org.phone,
  };
  if (includeLanguages) {
    payload.defaultLanguageGuiId = idOf(org.defaultLanguageGui);
    payload.defaultLanguagePdfId = idOf(org.defaultLanguagePdf);
  }
  return payload;
}

export function toCustomizationPayload(org: Organisation): OrganisationCustomizationPayload {
  return {
    hasI18n: toBoolean(org.hasI18n),
    defaultLanguageGuiId: idOf(org.defaultLanguageGui),
    defaultLanguagePdfId: idOf(org.defaultLanguagePdf),
  };
}

/** Body of PUT /organisations/{id}/notifications (C2027-195). */
export interface OrganisationNotificationsPayload {
  sendAfterExpertValidation: boolean;
  notifyAdvisorsOnAvailability: boolean;
  notifyManagerOnAvailability: boolean;
  wanakaBroadcastsToFarmers: boolean;
  wanakaMakesVisible: boolean;
  forcedBroadcastDelay: ForcedBroadcastDelay;
}

export function toNotificationsPayload(org: Organisation): OrganisationNotificationsPayload {
  return {
    sendAfterExpertValidation: toBoolean(org.sendAfterExpertValidation),
    notifyAdvisorsOnAvailability: toBoolean(org.notifyAdvisorsOnAvailability),
    notifyManagerOnAvailability: toBoolean(org.notifyManagerOnAvailability),
    wanakaBroadcastsToFarmers: toBoolean(org.wanakaBroadcastsToFarmers),
    wanakaMakesVisible: toBoolean(org.wanakaMakesVisible),
    forcedBroadcastDelay: org.forcedBroadcastDelay || null,
  };
}

/** Body of PUT /organisations/{id}/authorizations (C2027-196). */
export interface OrganisationAuthorizationsPayload {
  farmerCanCreateFarm: boolean;
  farmerCanCreateParcel: boolean;
  farmerCanEditParcel: boolean;
  farmerCanCreateOrder: boolean;
  farmerCanEditOrder: boolean;
  farmerCanPlanDelivery: boolean;
}

export function toAuthorizationsPayload(org: Organisation): OrganisationAuthorizationsPayload {
  return {
    farmerCanCreateFarm: toBoolean(org.farmerCanCreateFarm),
    farmerCanCreateParcel: toBoolean(org.farmerCanCreateParcel),
    farmerCanEditParcel: toBoolean(org.farmerCanEditParcel),
    farmerCanCreateOrder: toBoolean(org.farmerCanCreateOrder),
    farmerCanEditOrder: toBoolean(org.farmerCanEditOrder),
    farmerCanPlanDelivery: toBoolean(org.farmerCanPlanDelivery),
  };
}
